/**
 * Shared DuckDB SQL helpers extracted from the worker so multiple builders
 * (`buildSql` for chart queries, `buildExportSql` for Excel export) stay in
 * lockstep on identifier escaping, value escaping, and filter-condition
 * shape. Pure functions — no DuckDB instance required.
 */
import type { FilterExpression } from '@/types/query';

/** Escape a DuckDB identifier (table / column name). */
export function escId(name: string): string {
  return '"' + name.replace(/"/g, '""') + '"';
}

/** Escape a SQL literal — strings get single-quote doubling. */
export function escVal(v: unknown): string {
  if (v === null || v === undefined) return 'NULL';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return "'" + String(v).replace(/'/g, "''") + "'";
}

/**
 * Escape a SQL string literal. Use this when the caller has a value that
 * is *already* known to be a string and doesn't need the NULL / number /
 * boolean type-dispatch that {@link escVal} performs.
 */
export function escStr(v: string): string {
  return "'" + v.replace(/'/g, "''") + "'";
}

/**
 * Build a single SQL condition for a filter expression. `colExpr` lets the
 * caller wrap the raw column reference in a CASE expression (e.g. when a
 * value transform is applied) so filter values match the same labels the
 * UI displays.
 */
export function buildCondition(
  f: FilterExpression,
  colExpr: (col: string) => string = escId,
): string {
  const col = colExpr(f.column);
  switch (f.operator) {
    case 'eq':
      return `${col} = ${escVal(f.value)}`;
    case 'neq':
      return `${col} != ${escVal(f.value)}`;
    case 'gt':
      return `${col} > ${escVal(f.value)}`;
    case 'gte':
      return `${col} >= ${escVal(f.value)}`;
    case 'lt':
      return `${col} < ${escVal(f.value)}`;
    case 'lte':
      return `${col} <= ${escVal(f.value)}`;
    case 'contains':
      return `${col} ILIKE ${escVal('%' + String(f.value) + '%')}`;
    case 'not_contains':
      return `${col} NOT ILIKE ${escVal('%' + String(f.value) + '%')}`;
    case 'in':
      return `${col} IN (${(f.value as Array<string | number>).map(escVal).join(', ')})`;
    case 'not_in':
      return `${col} NOT IN (${(f.value as Array<string | number>).map(escVal).join(', ')})`;
    case 'between': {
      const [lo, hi] = f.value as [string | number, string | number];
      return `${col} BETWEEN ${escVal(lo)} AND ${escVal(hi)}`;
    }
    case 'is_null':
      return `${col} IS NULL`;
    case 'is_not_null':
      return `${col} IS NOT NULL`;
    default:
      throw new Error(`Unknown filter operator: ${(f as FilterExpression).operator}`);
  }
}
